import { useEffect, useRef, useState } from 'react'
import type { Asset, AudioClip, Composition } from '@shared/types'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { getAudioEndSec, getVideoDurationSec, secToPx } from '../../../lib/timelineMath'
import { AUDIO_LANE_H } from './constants'

/** task spec §2-6: フェード秒の上限 = min(5, クリップ尺/2)。 */
function maxFadeSec(durationSec: number): number {
  return Math.max(0, Math.min(5, durationSec / 2))
}

function roundFadeSec(v: number, durationSec: number): number {
  const clamped = Math.min(maxFadeSec(durationSec), Math.max(0, v))
  return Math.round(clamped * 10) / 10
}

/**
 * task spec §2-11: BGM/SFX の波形。builtinId（server/assets/audio/ 配下・HTTP
 * 経由で取れない）はここでは decode 不能 — フラットブロック＋「波形なし」で
 * 隠さず表示する。デコード結果は URL キーでキャッシュして重複デコードを避ける。
 */
const peakCache = new Map<string, Float32Array | 'error'>()

async function decodePeaks(url: string, buckets = 160): Promise<Float32Array | 'error'> {
  try {
    const res = await fetch(url)
    const arrayBuffer = await res.arrayBuffer()
    const ctx = new AudioContext()
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
    const data = audioBuffer.getChannelData(0)
    const peaks = new Float32Array(buckets)
    const step = Math.max(1, Math.floor(data.length / buckets))
    for (let i = 0; i < buckets; i++) {
      let max = 0
      const start = i * step
      const end = Math.min(data.length, start + step)
      for (let j = start; j < end; j++) {
        const v = Math.abs(data[j])
        if (v > max) max = v
      }
      peaks[i] = max
    }
    void ctx.close()
    return peaks
  } catch {
    return 'error'
  }
}

function Waveform({ url }: { url: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [peaks, setPeaks] = useState<Float32Array | 'error' | null>(peakCache.get(url) ?? null)

  useEffect(() => {
    let cancelled = false
    const cached = peakCache.get(url)
    if (cached) {
      setPeaks(cached)
      return
    }
    decodePeaks(url).then((p) => {
      // UI_MK2_PHASE2.md §2-7 (reviewer minor): 'error' はキャッシュへ保存しない
      // — 一時的な fetch/decode 失敗（ネットワーク瞬断等）を永続的な「波形なし」
      // として固定してしまわないよう、次回マウント時に再デコードを試みる。
      if (p !== 'error') peakCache.set(url, p)
      if (!cancelled) setPeaks(p)
    })
    return () => {
      cancelled = true
    }
  }, [url])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !peaks || peaks === 'error') return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#35C7E0'
    const barW = canvas.width / peaks.length
    for (let i = 0; i < peaks.length; i++) {
      const h = Math.max(1, peaks[i] * canvas.height)
      ctx.fillRect(i * barW, (canvas.height - h) / 2, Math.max(1, barW - 0.5), h)
    }
  }, [peaks])

  if (peaks === 'error') {
    return (
      <div
        className="w-full h-full flex items-center justify-center text-[9px] text-text-faint"
        data-testid="audio-waveform-none"
      >
        波形なし
      </div>
    )
  }
  return <canvas ref={canvasRef} width={400} height={28} className="w-full h-full" data-testid="audio-waveform" />
}

/** task spec §2-6: フェード区間に重ねる半透明の三角ランプ（長さが見える）。 */
function FadeRamp({ side, widthPx }: { side: 'in' | 'out'; widthPx: number }) {
  if (widthPx <= 0) return null
  const clipPath =
    side === 'in'
      ? 'polygon(0% 0%, 0% 100%, 100% 100%)' // 左端で最大減衰 → フェード終端でゼロ
      : 'polygon(100% 0%, 100% 100%, 0% 100%)' // 右端で最大減衰 → フェード始端でゼロ
  return (
    <div
      className="absolute top-0 bottom-0 bg-black/55 pointer-events-none"
      style={{ [side === 'in' ? 'left' : 'right']: 0, width: widthPx, clipPath }}
      data-testid={`audio-fade-ramp-${side}`}
    />
  )
}

type FadeDragMode = 'fade-in' | 'fade-out'

function AudioClipBlock({
  clip,
  left,
  width,
  durationSec,
  url,
  selected,
  pxPerSec,
}: {
  clip: AudioClip
  left: number
  width: number
  durationSec: number
  url: string | null
  selected: boolean
  pxPerSec: number
}) {
  const select = useEditorStore((s) => s.select)
  const toggleSelection = useEditorStore((s) => s.toggleSelection)
  const updateAudio = useEditorStore((s) => s.updateAudio)
  const beginHistoryBatch = useEditorStore((s) => s.beginHistoryBatch)
  const endHistoryBatch = useEditorStore((s) => s.endHistoryBatch)
  const drag = useRef<{ mode: FadeDragMode; x: number; initialSec: number } | null>(null)

  function endDrag(e: React.PointerEvent) {
    if (drag.current) {
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      endHistoryBatch()
    }
    drag.current = null
  }

  function beginFadeDrag(mode: FadeDragMode, e: React.PointerEvent) {
    e.stopPropagation()
    drag.current = { mode, x: e.clientX, initialSec: mode === 'fade-in' ? clip.fadeInSec : clip.fadeOutSec }
    beginHistoryBatch()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  function onFadeDragMove(e: React.PointerEvent) {
    if (!drag.current) return
    e.stopPropagation()
    const deltaSec = (e.clientX - drag.current.x) / pxPerSec
    // フェードインは右へ引くほど長く、フェードアウトは左へ引くほど長くなる。
    const signedDelta = drag.current.mode === 'fade-in' ? deltaSec : -deltaSec
    const next = roundFadeSec(drag.current.initialSec + signedDelta, durationSec)
    if (drag.current.mode === 'fade-in') {
      if (next !== clip.fadeInSec) updateAudio(clip.id, { fadeInSec: next })
    } else if (next !== clip.fadeOutSec) {
      updateAudio(clip.id, { fadeOutSec: next })
    }
  }

  const fadeInPx = Math.min(width / 2, secToPx(clip.fadeInSec, pxPerSec))
  const fadeOutPx = Math.min(width / 2, secToPx(clip.fadeOutSec, pxPerSec))

  return (
    <div
      data-testid="audio-clip"
      data-audio-id={clip.id}
      className={`absolute top-1 bottom-1 rounded bg-accent-blue/25 border ${
        selected ? 'border-white' : 'border-accent-blue/50'
      }`}
      style={{ left, width }}
      title={`${clip.kind} ${clip.gainDb}dB`}
      onClick={(e) => {
        e.stopPropagation()
        if (e.shiftKey) toggleSelection({ kind: 'audio', id: clip.id })
        else select({ kind: 'audio', id: clip.id })
      }}
    >
      {/*
        UI_MK2_PHASE2.md §2-6: `overflow-hidden` はこの内側だけに掛ける。
        フェードハンドル（下記）はクリップ上端の外側へ半分はみ出す配置
        （`-top-0.5` / `-translate-x-1/2`）なので、外側の箱ごとクリップすると
        はみ出した半分が「見た目は描画されるが getBoundingClientRect の中心
        座標では document.elementFromPoint がヒットしない」状態になり、
        Playwright（実ブラウザ）のポインタ操作が下の audio-lane へ抜けて
        ドラッグが一切効かなくなる実測バグがあった。
      */}
      <div className="absolute inset-0 rounded overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1 z-10 text-[9px] text-accent-blue px-0.5 bg-ink-surface2/70 rounded-sm">
          {clip.kind === 'bgm' ? 'BGM' : 'SFX'}
        </div>
        {url ? (
          <Waveform url={url} />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-[9px] text-text-faint"
            data-testid="audio-waveform-none"
          >
            波形なし
          </div>
        )}
        <FadeRamp side="in" widthPx={fadeInPx} />
        <FadeRamp side="out" widthPx={fadeOutPx} />
      </div>
      {/* task spec §2-6: クリップ上端左右の○ハンドルをドラッグでフェード長 */}
      <div
        onPointerDown={(e) => beginFadeDrag('fade-in', e)}
        onPointerMove={onFadeDragMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="audio-fade-in-handle"
        title={`フェードイン ${clip.fadeInSec.toFixed(1)}s`}
        className="absolute -top-0.5 left-0 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-white border border-accent-blue cursor-ew-resize z-20 hover:scale-125"
      />
      <div
        onPointerDown={(e) => beginFadeDrag('fade-out', e)}
        onPointerMove={onFadeDragMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="audio-fade-out-handle"
        title={`フェードアウト ${clip.fadeOutSec.toFixed(1)}s`}
        className="absolute -top-0.5 right-0 translate-x-1/2 w-2.5 h-2.5 rounded-full bg-white border border-accent-blue cursor-ew-resize z-20 hover:scale-125"
      />
    </div>
  )
}

export function AudioLane({
  composition,
  assets,
  pxPerSec,
}: {
  composition: Composition
  assets: Record<string, Asset>
  pxPerSec: number
}) {
  const select = useEditorStore((s) => s.select)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const clips = composition.tracks.audio
  const videoEnd = getVideoDurationSec(composition)

  return (
    <div
      className="relative"
      style={{ height: AUDIO_LANE_H }}
      data-testid="audio-lane"
      onClick={(e) => {
        if (e.target === e.currentTarget) select(null)
      }}
    >
      {clips.length === 0 && (
        <div className="absolute inset-0 flex items-center px-3 text-[11px] text-text-faint">音声なし</div>
      )}
      {clips.map((clip) => {
        const endSec = getAudioEndSec(clip, videoEnd)
        const durationSec = endSec - clip.startSec
        const left = secToPx(clip.startSec, pxPerSec)
        const width = Math.max(10, durationSec * pxPerSec)
        // builtinId (server/assets/audio/ 配下) は /files 経由で公開されておらず
        // クライアントから decode できない — asset 由来のクリップだけ URL が引ける。
        const asset = clip.assetId ? assets[clip.assetId] : undefined
        const url = asset ? api.fileUrl(asset.proxyPath ?? asset.originalPath) : null
        return (
          <AudioClipBlock
            key={clip.id}
            clip={clip}
            left={left}
            width={width}
            durationSec={durationSec}
            url={url}
            pxPerSec={pxPerSec}
            selected={selectedIds.some((s) => s.kind === 'audio' && s.id === clip.id)}
          />
        )
      })}
    </div>
  )
}
